export const getFileIconClass = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'csv':
      return 'csv';
    default:
      return 'file';
  }
};

export const getFileIconText = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'csv':
      return 'CSV';
    default:
      return 'FILE';
  }
};

// Re-export formatFileSize from shared utility
export { formatFileSize } from '../../utils/fileIcons';

import { formatDateISO } from '../../utils/dateUtils';

export const formatDate = (dateString: string): string => {
  return formatDateISO(dateString);
};

export const isCsvFile = (fileName: string): boolean => {
  return fileName.split('.').pop()?.toLowerCase() === 'csv';
};

export const isPlottableFile = (fileName: string): boolean => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  return ext === 'csv' || ext === 'bin';
};

/** One file as the Analytics handoff names it. */
export interface AnalyticsHandoffFile {
  /**
   * The object's path **inside the caller's own organization**.
   *
   * Absent for an object the caller does not own — see
   * `analyticsHandoffPayload`. Present for every own file, unchanged.
   */
  s3_key?: string;
  /**
   * The name to show, and to route CSV-vs-BIN off.
   *
   * **This is the only source of the name** (#1297). `analyticsHandoffPayload`
   * does not derive one from `s3_key` under any condition — the caller knows the
   * row; the key since #39 Phase C is a uuid that knows nothing. A caller that
   * passes `''` gets `''`, which the Analytics side accepts as a string; naming
   * the file is the caller's job and there is no second guess here.
   */
  filename: string;
  /**
   * The `fleet.file_objects` row id, when known.
   *
   * Additive, exactly as `analytics-backend`'s `/api/s3/list-files` added it:
   * an id addresses one row, whereas a path is only meaningful relative to an
   * organization. It is the only thing that can name a SHARED object across the
   * boundary, and the identity #1231 wants passed instead of a reconstructed
   * path.
   */
  object_id?: string;
}

/**
 * The payload the `#s3Keys=` fragment carries, as a pure value.
 *
 * Extracted from `openAnalyticsWithFiles` so the rule below can be tested — it
 * is a rule about *which file the other application will open*, and getting it
 * wrong is silent.
 *
 * ### Why a shared entry carries no `s3_key`
 *
 * `analytics-backend/routers/s3.py` scopes its listing to the caller's own
 * `organization_id` **on top of** RLS, and says why: grafting a foreign subtree
 * into this organization's path namespace would render two trees into one set of
 * paths — two files at `logs/flight.csv`, one of them somebody else's. So a path
 * handed over for a shared file is resolved in the WRONG namespace. The failure
 * is usually a 404; the failure that matters is the other one, where the caller
 * happens to own a file at the same relative path and silently plots it instead.
 *
 * Sending the id alone makes that unexpressible. An Analytics build that does not
 * yet read `object_id` rejects the entry and says so, which is the honest
 * outcome — far better than opening the wrong flight on a screen about somebody
 * else's data.
 */
export const analyticsHandoffPayload = (
  files: AnalyticsHandoffFile[],
  orgSlug: string | null
): AnalyticsHandoffFile[] => {
  // Normalize S3 keys: strip org prefix if present (analytics backend prepends it back)
  const orgPrefix = orgSlug ? `organizations/${orgSlug}/` : null;
  return files.map((file) => {
    if (file.s3_key === undefined) {
      return { filename: file.filename, object_id: file.object_id };
    }
    // The prefix strip. **Not idempotent in principle**, and worth knowing why it
    // is safe in practice rather than assuming it: `flightHandoffEntry` already
    // stripped once via `relativeLegacyKey`, so this strips a second time, and a
    // key shaped `organizations/<slug>/2026/organizations/<slug>/f.csv` would
    // lose a genuine path segment here. Audited read-only against prod
    // (2026-08-11) and it is fully theoretical: **0** flights with `object_id`
    // NULL — so nothing reaches this key arm at all — **0** keys matching
    // `^organizations/[^/]+/organizations/`, and **0** keys containing
    // `organizations/` anywhere past position 0. Left alone deliberately: a guard
    // would have to ask what a key looks like, which is the habit this arc exists
    // to break.
    const s3_key =
      orgPrefix && file.s3_key.startsWith(orgPrefix)
        ? file.s3_key.slice(orgPrefix.length)
        : file.s3_key;
    // **The caller names the file. The key never does** (#1297).
    //
    // This was `s3_key.split('/').pop() || file.filename` — a key-derived fact
    // beating a row-derived one, the shape #39 Phase C invalidated. A minted uuid
    // has no `/`, so the pop returned the uuid itself; truthy, so the
    // `|| file.filename` fallback never fired and a correct `log_basename` chosen
    // by `flightHandoffEntry` was overwritten on the way out. The viewer routes
    // CSV-vs-BIN off this string, so the name is load-bearing beyond display.
    //
    // The first fix reversed the precedence and kept the pop as a fallback. **The
    // fallback is gone because it was dead**, traced across all four callers:
    // `useFileOperations` gates on `isPlottableFile(key)` so its key has an
    // extension and its derived name is non-empty; `SharedView` returns early
    // unless `entry.name` is truthy and sends no key at all; the two
    // `flightHandoffEntry` callers name the file `log_basename ||
    // relative.split('/').pop() || relative`, which is `''` only when `relative`
    // is `''`. So at every caller a non-empty `s3_key` implies a non-empty
    // `filename`, and the pop could never supply a name the caller did not
    // already have. Keeping a dead key-derivation alive as a documented exemption
    // is worse than having no fallback — a standing permission reads as sanction
    // and widens what the next person thinks is allowed, which is the Python
    // ratchet's own staleness rule applied here. `filename` is `string` on the
    // interface, so `''` in still means `''` out, exactly as before.
    const filename = file.filename;
    return file.object_id ? { s3_key, filename, object_id: file.object_id } : { s3_key, filename };
  });
};

// Same-origin handoff (#617): Analytics SPA reads org + auth from the JWT in
// shared localStorage. Only the s3Keys fragment is passed via URL — it's the
// navigation payload, not auth.
export const openAnalyticsWithFiles = (
  files: AnalyticsHandoffFile[],
  orgSlug: string | null
): void => {
  const keysJson = JSON.stringify(analyticsHandoffPayload(files, orgSlug));
  const keysEncoded = btoa(unescape(encodeURIComponent(keysJson)));

  const url = `/analytics/#s3Keys=${keysEncoded}`;

  window.open(url, '_blank', 'noopener,noreferrer');
};
